import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { services, siteConfig } from '../../data/mock';
import {
  Search,
  Globe,
  Server,
  Puzzle,
  Link as LinkIcon,
  Cloud,
  ShieldCheck,
  Lock,
  Workflow,
  ArrowRight,
  MessageCircle,
  Layers,
  CheckCircle2,
} from 'lucide-react';

const iconMap = {
  Search,
  Globe,
  Server,
  Puzzle,
  Link: LinkIcon,
  Cloud,
  ShieldCheck,
  Lock,
  Workflow,
};

export const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);

  const handleWhatsApp = (serviceName) => {
    const message = `Olá! Tenho interesse no serviço de ${serviceName} da KC Byte iT.`;
    window.open(
      `https://wa.me/${siteConfig.whatsapp}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  return (
    <main className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-6">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#329780]/10 border border-[#329780]/20">
              <Layers className="w-4 h-4 text-[#329780] mr-2" />
              <span className="text-[#329780] text-sm font-medium">
                Nossos Serviços
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Soluções completas em{' '}
              <span className="text-[#329780]">tecnologia</span>
            </h1>

            <p className="text-lg text-slate-400 leading-relaxed">
              Oferecemos um portfólio diversificado de serviços de TI, desde
              segurança cibernética até automação de processos. Encontre a solução
              ideal para seu negócio.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card
                  key={service.id}
                  className="bg-[#111920] border-[#1e293b] card-hover group relative overflow-hidden"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardContent className="p-6 space-y-5">
                    {/* Icon */}
                    <div className="w-14 h-14 rounded-xl bg-[#329780]/10 flex items-center justify-center group-hover:bg-[#329780]/20 transition-colors duration-300">
                      {IconComponent && (
                        <IconComponent className="w-7 h-7 text-[#329780]" />
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white leading-tight">
                      {service.title}
                    </h3>

                    {/* Description */}
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {service.description}
                    </p>

                    {/* Benefit */}
                    <div className="flex items-center space-x-2 text-[#329780]">
                      <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium">{service.benefit}</span>
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={() => handleWhatsApp(service.title)}
                      variant="outline"
                      className="w-full border-[#329780]/30 text-[#329780] hover:bg-[#329780]/10 hover:border-[#329780]/50 mt-2"
                    >
                      Quero esse serviço
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>

                  {/* Hover Accent */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#329780] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-[#080c10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Como <span className="text-[#329780]">trabalhamos</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Um processo estruturado para garantir os melhores resultados para
              seu projeto.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Entendimento',
                description:
                  'Analisamos suas necessidades e desafios para propor a melhor solução.',
              },
              {
                step: '02',
                title: 'Proposta',
                description:
                  'Elaboramos uma proposta detalhada com escopo, prazo e investimento.',
              },
              {
                step: '03',
                title: 'Execução',
                description:
                  'Implementamos a solução com entregas frequentes e acompanhamento.',
              },
              {
                step: '04',
                title: 'Suporte',
                description:
                  'Oferecemos suporte contínuo e evolução das soluções implementadas.',
              },
            ].map((item, index) => (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-full h-px bg-gradient-to-r from-[#329780]/50 to-transparent" />
                )}

                <div className="relative z-10 space-y-4">
                  <div className="w-16 h-16 rounded-2xl bg-[#329780]/10 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#329780]">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-[#111920] border border-[#1e293b] rounded-3xl p-8 sm:p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#329780]/10 rounded-full blur-3xl" />

            <div className="relative space-y-6">
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Não encontrou o que procura?
              </h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                Entre em contato conosco. Podemos desenvolver soluções
                personalizadas para atender às necessidades específicas do seu
                negócio.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <Link to="/contato">
                  <Button
                    size="lg"
                    className="bg-[#329780] hover:bg-[#287a68] text-white px-8 h-12"
                  >
                    Fale Conosco
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => handleWhatsApp('personalizado')}
                  className="border-slate-700 text-slate-300 hover:bg-white/5 px-8 h-12"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
